import React from "react";
import {
  Box,
  LinearProgress,
  Typography,
  Paper,
  Collapse,
  IconButton,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Sync as SyncIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Block as BlockIcon,
  Close as CloseIcon,
} from "@material-ui/icons";
import { green, orange, red, blue, grey } from "@material-ui/core/colors";

const useStyles = makeStyles((theme) => ({
  root: {
    position: "relative",
    marginBottom: theme.spacing(1),
  },
  paper: {
    padding: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  progressContainer: {
    flex: 1,
  },
  progressBar: {
    height: 10,
    borderRadius: 5,
  },
  progressText: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: theme.spacing(0.5),
  },
  iconSyncing: {
    color: blue[500],
    animation: "$spin 1s linear infinite",
  },
  iconProcessing: {
    color: orange[500],
    animation: "$spin 2s linear infinite",
  },
  iconCompleted: {
    color: green[500],
  },
  iconError: {
    color: red[500],
  },
  iconDisabled: {
    color: grey[500],
  },
  statsText: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
  },
  "@keyframes spin": {
    "0%": {
      transform: "rotate(0deg)",
    },
    "100%": {
      transform: "rotate(360deg)",
    },
  },
}));

const WhatsAppSyncProgress = ({ syncData, onClose }) => {
  const classes = useStyles();

  if (!syncData) return null;

  const {
    progress = 0,
    chats = 0,
    contacts = 0,
    messages = 0,
    status = "syncing",
    statusMessage = "",
  } = syncData;

  const getIcon = () => {
    switch (status) {
      case "syncing":
        return <SyncIcon className={classes.iconSyncing} />;
      case "processing":
        return <SyncIcon className={classes.iconProcessing} />;
      case "completed":
        return <CheckCircleIcon className={classes.iconCompleted} />;
      case "error":
        return <ErrorIcon className={classes.iconError} />;
      case "disabled":
        return <BlockIcon className={classes.iconDisabled} />;
      default:
        return <SyncIcon className={classes.iconSyncing} />;
    }
  };

  const getProgressColor = () => {
    switch (status) {
      case "completed":
        return "primary";
      case "error":
        return "secondary";
      default:
        return "primary";
    }
  };

  const getStatusText = () => {
    if (statusMessage) return statusMessage;

    switch (status) {
      case "syncing":
        return `Sincronizando histórico... ${progress}%`;
      case "processing":
        return `Processando mensagens...`;
      case "completed":
        return "Sincronização concluída!";
      case "error":
        return "Erro na sincronização";
      case "disabled":
        return "Importação desabilitada";
      default:
        return "Sincronizando...";
    }
  };

  const showProgress = status === "syncing" || status === "processing";

  return (
    <Collapse in={true}>
      <Box className={classes.root}>
        <Paper className={classes.paper} elevation={2}>
          {getIcon()}

          <Box className={classes.progressContainer}>
            <Typography variant="body2" gutterBottom>
              {getStatusText()}
            </Typography>

            {showProgress && (
              <>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  color={getProgressColor()}
                  className={classes.progressBar}
                />
                <Box className={classes.progressText}>
                  <Typography className={classes.statsText}>
                    {chats} chats | {contacts} contatos | {messages} mensagens
                  </Typography>
                  <Typography className={classes.statsText}>
                    {progress}%
                  </Typography>
                </Box>
              </>
            )}
          </Box>

          {(status === "completed" || status === "error" || status === "disabled") && onClose && (
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Paper>
      </Box>
    </Collapse>
  );
};

export default WhatsAppSyncProgress;
